import { InvalidInput, NotFound, resetPassword } from '@trippath/shared';
import { useId, useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { problemKey } from '../../lib/format';

/** How strong a password looks, 0 to 4: long enough, then longer, and mixing kinds of letters. */
export function strength(password: string): number {
  if (password.length < 10) return 0;
  const kinds = [/[a-z]/, /[A-Z]/, /\d/, /[^a-zA-Z\d]/].filter((kind) =>
    kind.test(password),
  ).length;
  return Math.min(
    4,
    1 + Number(password.length >= 14) + Number(kinds >= 3) + Number(password.length >= 20),
  );
}

/** A new password set by a mailed link (the `ShareLink` mockup's last step). */
export function Reset() {
  const { t } = useTranslation();
  const ids = useId();
  const { token = '' } = useParams();
  const [password, setPassword] = useState('');
  const [others, setOthers] = useState(true);
  const [state, setState] = useState<'typing' | 'done' | 'gone' | 'failed'>('typing');
  const [error, setError] = useState<string | null>(null);
  const score = strength(password);
  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    setError(null);
    resetPassword(token, password, others).then(
      () => {
        setState('done');
      },
      (failure: unknown) => {
        if (failure instanceof InvalidInput) {
          setError(failure.fields.map((field) => t(problemKey(field.problem))).join(' '));
        } else {
          setState(failure instanceof NotFound ? 'gone' : 'failed');
        }
      },
    );
  };
  if (state === 'done' || state === 'gone') {
    return (
      <div className="page sign-in">
        <div className="panel form">
          <h1>{t('reset.newTitle')}</h1>
          <p role="status">{t(state === 'done' ? 'reset.done' : 'reset.gone')}</p>
          <Link className="button button-primary" to={state === 'done' ? '/' : '/forgot'}>
            {t(state === 'done' ? 'reset.signIn' : 'reset.again')}
          </Link>
        </div>
      </div>
    );
  }
  return (
    <div className="page sign-in">
      <form className="panel form" onSubmit={submit} aria-labelledby={`${ids}-title`}>
        <h1 id={`${ids}-title`}>{t('reset.newTitle')}</h1>
        <div className="field">
          <label className="field-label" htmlFor={`${ids}-password`}>
            {t('reset.password')}
          </label>
          <input
            id={`${ids}-password`}
            className="text-input"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            aria-invalid={error !== null}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />
          {error !== null && <p className="field-error">{error}</p>}
        </div>
        <div
          className={`reset-meter reset-meter-${String(score)}`}
          role="meter"
          aria-valuemin={0}
          aria-valuemax={4}
          aria-valuenow={score}
          aria-label={t('reset.strength', { said: t(`reset.strong.${String(score)}`) })}
        >
          <span />
        </div>
        <p className="quiet small">{t('reset.hint')}</p>
        <label className="check">
          <input
            type="checkbox"
            checked={others}
            onChange={(event) => {
              setOthers(event.target.checked);
            }}
          />
          {t('reset.others')}
        </label>
        {state === 'failed' && (
          <p className="form-failed" role="alert">
            {t('trips.failed')}
          </p>
        )}
        <button type="submit" className="button button-primary">
          {t('reset.save')}
        </button>
      </form>
    </div>
  );
}
